import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  useGetGroupingOverviewQuery,
  useGetGroupingWebsiteQuery,
  useGetGroupingLogsQuery,
  useGetGroupingHistoryQuery,
  useRegroupWebsiteMutation,
  useRegroupAllPartialsMutation,
} from "../../../app/api/console/agentOperationsApiSlice";

const STATUS_STYLES = {
  grouped: "bg-green-100 text-green-700",
  partial: "bg-amber-100 text-amber-700",
  never_grouped: "bg-red-100 text-red-700",
  no_findings: "bg-surface-neutral text-text-tertiary",
};
const STATUS_LABEL = {
  grouped: "grouped",
  partial: "partial",
  never_grouped: "never",
  no_findings: "no findings",
};

const FILTERS = [
  { id: "all", label: "All" },
  { id: "partial", label: "Partial" },
  { id: "never_grouped", label: "Never" },
  { id: "grouped", label: "Grouped" },
  { id: "no_findings", label: "No findings" },
];

export default function GroupingView({ onToast }) {
  const [env, setEnv] = useState("staging");
  const { data, isLoading, refetch, isFetching } = useGetGroupingOverviewQuery(env, {
    pollingInterval: 30000,
  });
  const [regroup] = useRegroupWebsiteMutation();
  const [regroupAll] = useRegroupAllPartialsMutation();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [openRow, setOpenRow] = useState(null);
  const [busy, setBusy] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  // Not memoised on purpose: `data.websites` is already a stable RTK Query reference and
  // the `[]` fallback only applies while the overview is loading; wrapping it in useMemo
  // would change its identity semantics for the `rows` memo below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const websites = data?.websites || [];
  const summary = data?.summary || {};
  // Per-row actions must target the env the displayed rows actually belong to,
  // NOT the toggle state. Website IDs differ across envs, and right after an
  // env switch the toggle flips before the new overview lands — so reading the
  // toggle would send a stale (e.g. staging) website_id to the new (prod) env
  // and 404. The overview payload is stamped with its own env, so bind to that.
  const activeEnv = data?.env || env;
  // The displayed rows belong to ``data.env``. Only act when that matches the
  // selected toggle — otherwise a row's (e.g. staging) website_id would be sent
  // to the just-selected (prod) env and 404 (the cross-env failure seen in
  // prod). During an env switch the old payload lingers until the new one
  // lands, so gate every per-row action on this readiness flag.
  const envReady = data?.env === env;

  const rows = useMemo(() => {
    const f = search.toLowerCase();
    return websites.filter((w) => {
      if (filter !== "all" && w.status !== filter) return false;
      if (!f) return true;
      return (
        (w.website_name || "").toLowerCase().includes(f) ||
        (w.website_url || "").toLowerCase().includes(f) ||
        (w.website_id || "").toLowerCase().includes(f)
      );
    });
  }, [websites, search, filter]);

  const doRegroup = async (websiteId, mode) => {
    if (!envReady) {
      onToast?.({ kind: "bad", msg: "Environment is still switching — wait for the list to reload before regrouping." });
      return;
    }
    if (mode === "from_scratch") {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Rebuild grouping FROM SCRATCH on ${activeEnv.toUpperCase()}? This deletes all pattern cards for this website and re-ranks everything.`)) return;
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Are you sure? This is DESTRUCTIVE on ${activeEnv.toUpperCase()} and regenerates slugs (deep links can break).`)) return;
    }
    setBusy(`${websiteId}:${mode}`);
    try {
      const res = await regroup({ websiteId, mode, env: activeEnv }).unwrap();
      const r = res.result || {};
      if (r.scheduled === false) {
        onToast?.({ kind: "bad", msg: `Not scheduled: ${r.reason || "nothing to group"}` });
      } else {
        const del = res.deleted_groups != null ? ` (deleted ${res.deleted_groups} cards)` : "";
        onToast?.({ kind: "ok", msg: `Regroup (${res.mode}) triggered${del}.` });
      }
    } catch (e) {
      onToast?.({ kind: "bad", msg: e?.data?.detail || "Regroup failed" });
    } finally {
      setBusy(null);
    }
  };

  const doRegroupAll = async () => {
    if (!envReady) {
      onToast?.({ kind: "bad", msg: "Environment is still switching — wait for the list to reload." });
      return;
    }
    const n = summary.partial || 0;
    if (!n) {
      onToast?.({ kind: "bad", msg: "No partial websites to regroup." });
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Queue an incremental regroup for all ${n} PARTIAL websites on ${activeEnv.toUpperCase()}? They run a few at a time through the queue.`)) return;
    setBulkBusy(true);
    try {
      const res = await regroupAll(activeEnv).unwrap();
      onToast?.({ kind: "ok", msg: `Queued ${res.queued} regroup${res.queued === 1 ? "" : "s"}.` });
    } catch (e) {
      onToast?.({ kind: "bad", msg: e?.data?.detail || "Bulk regroup failed" });
    } finally {
      setBulkBusy(false);
    }
  };

  const thClass =
    "px-3 py-2 text-left text-caption font-semibold text-text-secondary uppercase tracking-[0.06em]";

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Card label="Websites" value={summary.total} onClick={() => setFilter("all")} active={filter === "all"} />
        <Card label="Grouped" value={summary.grouped} tone="green" onClick={() => setFilter("grouped")} active={filter === "grouped"} />
        <Card label="Partial" value={summary.partial} tone="amber" onClick={() => setFilter("partial")} active={filter === "partial"} />
        <Card label="Never" value={summary.never_grouped} tone="red" onClick={() => setFilter("never_grouped")} active={filter === "never_grouped"} />
        <Card label="No findings" value={summary.no_findings} onClick={() => setFilter("no_findings")} active={filter === "no_findings"} />
        <Card label="Ungrouped" value={summary.ungrouped_findings} tone="amber" />
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Env switch */}
        <div className="inline-flex rounded-xl border border-border-default overflow-hidden">
          {["staging", "prod"].map((e) => (
            <button
              key={e}
              onClick={() => {
                setEnv(e);
                setOpenRow(null);
              }}
              className={`px-3 py-2 text-body-xs font-semibold capitalize transition-colors ${
                env === e
                  ? e === "prod"
                    ? "bg-red-600 text-white"
                    : "bg-button-primary text-white"
                  : "bg-white text-text-secondary hover:bg-surface-neutral"
              }`}>
              {e}
            </button>
          ))}
        </div>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search websites…"
          className="flex-1 max-w-xs px-3 py-2 text-body-xs bg-white border border-border-default rounded-xl focus:outline-none focus:ring-2 focus:ring-accent-purple/20"
        />
        <div className="flex gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-2.5 py-1.5 text-[12px] rounded-lg border ${
                filter === f.id
                  ? "bg-button-primary text-white border-button-primary"
                  : "bg-white text-text-secondary border-border-default hover:bg-surface-neutral"
              }`}>
              {f.label}
            </button>
          ))}
        </div>
        {typeof data?.age_seconds === "number" && (
          <span className="text-[11px] text-text-tertiary" title="Served from cache; auto-refreshes in the background">
            updated {data.age_seconds < 60 ? `${data.age_seconds}s` : `${Math.floor(data.age_seconds / 60)}m`} ago
          </span>
        )}
        <button
          onClick={() => refetch()}
          className="p-2 border border-border-default rounded-xl text-text-secondary hover:bg-surface-neutral"
          title="Refresh">
          <svg className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
        </button>
        <button
          disabled={!envReady || bulkBusy || !summary.partial}
          onClick={doRegroupAll}
          className="px-3 py-2 text-body-xs font-semibold rounded-xl border border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 disabled:opacity-40"
          title={envReady ? "Queue an incremental regroup for every partial website (throttled)" : "Waiting for the selected env to load…"}>
          ↻ Regroup all partials{summary.partial ? ` (${summary.partial})` : ""}
        </button>
      </div>

      {/* Table + global history */}
      <div className="flex flex-col xl:flex-row gap-4 items-start">
      <div className="flex-1 min-w-0 bg-white border border-border-default rounded-2xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-surface-neutral/40">
            <tr>
              <th className={thClass}>Website</th>
              <th className={thClass}>Status</th>
              <th className={`${thClass} text-right`}>Open</th>
              <th className={`${thClass} text-right`}>Ungrouped</th>
              <th className={`${thClass} text-right`}>Groups</th>
              <th className={thClass}>Memory</th>
              <th className={thClass}>Last grouped</th>
              <th className={`${thClass} text-right`}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-body-sm text-text-tertiary">Loading…</td></tr>
            )}
            {!isLoading && !rows.length && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-body-sm text-text-tertiary">No websites.</td></tr>
            )}
            {rows.map((w) => (
              <React.Fragment key={w.website_id}>
                <tr className="border-t border-border-subtle hover:bg-surface-neutral/30">
                  <td className="px-3 py-2">
                    <button
                      className="text-body-xs font-medium text-text-primary text-left hover:text-accent-purple"
                      onClick={() => setOpenRow(openRow === w.website_id ? null : w.website_id)}
                      title={w.website_id}>
                      {openRow === w.website_id ? "▾ " : "▸ "}{w.website_name || w.website_id}
                    </button>
                    <div className="text-[11px] text-text-tertiary truncate max-w-[220px]">{w.website_url}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_STYLES[w.status] || ""}`}>
                      {STATUS_LABEL[w.status] || w.status}
                    </span>
                    {w.orphan_memberships ? (
                      <span
                        className="ml-1 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-violet-50 text-violet-700 border border-violet-200"
                        title="Pattern cards exist but finding_group_members were wiped — counts use denormalized finding_count. Scratch or Regroup to rebuild memberships.">
                        orphan
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-body-xs text-right tabular-nums">{w.open_findings}</td>
                  <td className={`px-3 py-2 text-body-xs text-right tabular-nums ${w.ungrouped_findings > 0 ? "text-amber-600 font-semibold" : "text-text-tertiary"}`}>{w.ungrouped_findings}</td>
                  <td className="px-3 py-2 text-body-xs text-right tabular-nums">
                    {w.groups_count}{w.ranked_groups ? <span className="text-text-tertiary"> ★{w.ranked_groups}</span> : null}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <MemBadge on={(w.memory || []).includes("BUSINESS.md")} label="BIZ" />
                      <MemBadge on={(w.memory || []).includes("PREFERENCES.md")} label="PREF" />
                      <Link to="/admin/agent-memory" className="text-[11px] text-accent-purple hover:underline ml-1" title="Open Agent Memory">↗</Link>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-[11px] text-text-tertiary">{w.last_grouped_at ? new Date(w.last_grouped_at).toLocaleDateString() : "—"}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        disabled={!envReady || busy === `${w.website_id}:incremental` || (w.ungrouped_findings === 0 && !w.orphan_memberships)}
                        onClick={() => doRegroup(w.website_id, "incremental")}
                        className="px-2.5 py-1 text-[11px] font-semibold rounded-lg border border-border-default text-text-secondary hover:bg-surface-neutral disabled:opacity-40"
                        title={
                          !envReady
                            ? "Waiting for the selected env to load…"
                            : w.orphan_memberships
                              ? "Rebuild memberships for orphan pattern cards (incremental)"
                              : "Regroup remaining ungrouped findings (insertion)"
                        }>
                        ↻ Regroup
                      </button>
                      <button
                        disabled={!envReady || busy === `${w.website_id}:from_scratch` || w.groups_count === 0}
                        onClick={() => doRegroup(w.website_id, "from_scratch")}
                        className="px-2.5 py-1 text-[11px] font-semibold rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-40"
                        title={envReady ? "Delete all cards and rebuild from scratch (destructive)" : "Waiting for the selected env to load…"}>
                        ⟲ Scratch
                      </button>
                    </div>
                  </td>
                </tr>
                {openRow === w.website_id && (
                  <tr className="bg-surface-neutral/20">
                    <td colSpan={8} className="px-4 py-3">
                      <WebsiteDetail websiteId={w.website_id} env={activeEnv} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
        <GlobalHistoryPanel env={activeEnv} />
      </div>
    </div>
  );
}

function WebsiteDetail({ websiteId, env }) {
  const { data, isLoading } = useGetGroupingWebsiteQuery({ websiteId, env });
  if (isLoading) return <div className="text-body-xs text-text-tertiary">Loading…</div>;
  const groups = data?.groups || [];
  const ungrouped = data?.ungrouped || [];
  const orphan = Boolean(data?.orphan_memberships);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div>
        <div className="text-caption font-semibold text-text-secondary uppercase mb-1">Pattern cards ({groups.length})</div>
        {!groups.length && <div className="text-body-xs text-text-tertiary">No groups.</div>}
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {groups.map((g) => (
            <div key={g.id} className="flex items-center justify-between text-body-xs border border-border-subtle rounded-lg px-2 py-1.5 bg-white">
              <span className="truncate">
                <span className="text-text-tertiary tabular-nums">#{g.rank ?? "—"}</span>{" "}
                <span className="text-text-primary">{g.title || g.slug}</span>
              </span>
              <span className="text-text-tertiary shrink-0 ml-2">{g.finding_count ?? 0} · {g.score != null ? g.score : "—"}</span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="text-caption font-semibold text-text-secondary uppercase mb-1">Ungrouped findings ({orphan ? 0 : ungrouped.length})</div>
        {orphan ? (
          <div className="text-body-xs text-violet-700 bg-violet-50 border border-violet-200 rounded-lg px-2 py-1.5">
            Membership rows missing for these cards. Counts use stored finding_count — use Scratch or Regroup to rebuild memberships.
          </div>
        ) : (
          <>
            {!ungrouped.length && <div className="text-body-xs text-text-tertiary">None.</div>}
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {ungrouped.map((f) => (
                <div key={f.id} className="text-body-xs border border-border-subtle rounded-lg px-2 py-1.5 bg-white truncate" title={f.title}>
                  {f.title || f.slug || f.id}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <TriggerHistory websiteId={websiteId} env={env} />
    </div>
  );
}

function TriggerHistory({ websiteId, env }) {
  const { data, isLoading } = useGetGroupingLogsQuery({ websiteId, env });
  const logs = data?.logs || [];
  return (
    <div>
      <div className="text-caption font-semibold text-text-secondary uppercase mb-1">Trigger history ({logs.length})</div>
      {isLoading && <div className="text-body-xs text-text-tertiary">Loading…</div>}
      {!isLoading && !logs.length && (
        <div className="text-body-xs text-text-tertiary">No grouping runs logged yet.</div>
      )}
      <div className="space-y-1 max-h-64 overflow-y-auto">
        {logs.map((l, i) => (
          <div key={l.task_id || l.key || i} className="text-body-xs border border-border-subtle rounded-lg px-2 py-1.5 bg-white">
            <div className="flex items-center justify-between gap-2">
              <ModeBadge mode={l.mode} />
              <span className={`shrink-0 text-[11px] ${l.ok === false ? "text-red-600" : "text-text-tertiary"}`}>
                {l.ok === false ? "✕ failed" : "✓ ok"}
              </span>
            </div>
            <div className="flex items-center justify-between mt-0.5 text-[11px] text-text-tertiary">
              <span title={l.ts_iso}>{relTime(l.ts_iso)}</span>
              <span className="tabular-nums">{l.n_findings ?? "—"} → {l.n_groups ?? "—"} cards</span>
            </div>
            <div className="text-[11px] text-text-tertiary truncate">by {l.caller_id || "—"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GlobalHistoryPanel({ env }) {
  const { data } = useGetGroupingHistoryQuery(env, { pollingInterval: 5000 });
  const tasks = data?.tasks || [];
  const inflight = data?.inflight || 0;
  return (
    <div className="w-full xl:w-80 shrink-0 bg-white border border-border-default rounded-2xl p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-caption font-semibold text-text-secondary uppercase">Recent regroups</div>
        {inflight > 0 && (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-700">
            {inflight} running
          </span>
        )}
      </div>
      {!tasks.length && <div className="text-body-xs text-text-tertiary">No regroups triggered from here yet.</div>}
      <div className="space-y-1 max-h-[28rem] overflow-y-auto">
        {tasks.map((t) => (
          <div key={t.id} className="text-body-xs border border-border-subtle rounded-lg px-2 py-1.5">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-text-primary" title={t.website_id}>{t.website_name || t.website_id}</span>
              <RegroupStatusPill status={t.status} />
            </div>
            <div className="flex items-center justify-between mt-0.5 text-[11px] text-text-tertiary">
              <span>{t.mode === "from_scratch" ? "from scratch" : "incremental"}{t.batch_id ? " · bulk" : ""}</span>
              <span title={new Date((t.created_at || 0) * 1000).toISOString()}>{relTimeEpoch(t.created_at)}</span>
            </div>
            {t.triggered_by && (
              <div className="text-[11px] text-text-tertiary truncate">
                {t.batch_id ? "queued by" : "by"} {t.triggered_by}
              </div>
            )}
            {t.reason && <div className="text-[11px] text-amber-600 truncate" title={t.reason}>{t.reason}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

const REGROUP_STATUS_STYLES = {
  queued: "bg-surface-neutral text-text-tertiary",
  running: "bg-blue-100 text-blue-700",
  done: "bg-green-100 text-green-700",
  skipped: "bg-amber-100 text-amber-700",
  failed: "bg-red-100 text-red-700",
  timed_out: "bg-red-100 text-red-700",
};

function RegroupStatusPill({ status }) {
  return (
    <span className={`shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${REGROUP_STATUS_STYLES[status] || "bg-surface-neutral text-text-tertiary"}`}>
      {status}
    </span>
  );
}

function ModeBadge({ mode }) {
  const isFull = mode === "full";
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${isFull ? "bg-accent-purple/15 text-accent-purple" : "bg-amber-100 text-amber-700"}`}>
      {isFull ? "Regroup from 0" : mode === "insertion" ? "Insertion" : mode || "—"}
    </span>
  );
}

function relTime(iso) {
  if (!iso) return "—";
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return "—";
  return relTimeMs(ms);
}

function relTimeEpoch(sec) {
  if (!sec) return "—";
  return relTimeMs(sec * 1000);
}

function relTimeMs(ms) {
  const diff = Date.now() - ms;
  const s = Math.round(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

function Card({ label, value, tone, onClick, active }) {
  const toneCls =
    tone === "green" ? "text-green-700" : tone === "amber" ? "text-amber-600" : tone === "red" ? "text-red-600" : "text-text-primary";
  return (
    <button
      onClick={onClick}
      className={`text-left bg-white border rounded-xl px-3 py-2.5 ${active ? "border-accent-purple ring-1 ring-accent-purple/30" : "border-border-default"} ${onClick ? "hover:bg-surface-neutral cursor-pointer" : "cursor-default"}`}>
      <div className="text-caption text-text-tertiary uppercase tracking-[0.06em]">{label}</div>
      <div className={`text-h4 font-semibold ${toneCls}`}>{value ?? "—"}</div>
    </button>
  );
}

function MemBadge({ on, label }) {
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${on ? "bg-green-100 text-green-700" : "bg-surface-neutral text-text-tertiary"}`}>
      {label}
    </span>
  );
}
